import { App, Plugin } from "@pimo/pimo-app-builder";
import { getLocalizedMessage, SupportedLanguage } from "@pimo/pimo-l10n";

import type { HomePortalAppState } from "../app";
import { AdminPage } from "../components/admin-page";
import { APP_ROUTES } from "../constants";
import { BACKEND_URL } from "../env";
import { fetchAdminOverview } from "../helpers/fetch-admin-overview";
import { fetchUserProfile } from "../helpers/fetch-profile";
import { generateDebugMagicLink } from "../helpers/generate-debug-magic-link";
import { isPlatformAdminProfile } from "../helpers/get-user-role";
import { AdminOverview } from "../types/admin";

export interface AdminPluginState {
  adminOverview?: AdminOverview;
  isAdminLoading?: boolean;
  adminLoadError?: string;
}

export class AdminPlugin implements Plugin<HomePortalAppState> {
  private app?: App<HomePortalAppState>;

  onRegister(app: App<HomePortalAppState>): void {
    this.app = app;

    const view = app.createView({ name: "Admin" });
    const showDebugMagicLinkButton = import.meta.env.MODE !== "production";

    const adminComponent = view.addComponent({ component: AdminPage });

    adminComponent.mapState((state) => ({
      overview: state.adminOverview,
      isLoading: state.isAdminLoading ?? false,
      loadError: state.adminLoadError,
      userProfile: state.userProfile,
      language: state.language,
      showDebugMagicLinkButton,
    }));

    adminComponent.on("navigate-home", () => {
      this.app?.navigate(APP_ROUTES.homePortal);
    });

    adminComponent.on("logout", () => {
      window.location.href = `${BACKEND_URL}/keycloak/logout`;
    });

    adminComponent.on("refresh-overview", async () => {
      await this.loadOverview(app.getAppState().language);
    });

    adminComponent.on("change-language", async ({ payload }) => {
      if (!payload?.language) return;
      app.patchAppState({ language: payload.language });
    });

    adminComponent.on("generate-magic-link-debug", async () => {
      const state = app.getAppState();
      const clients = state.adminOverview?.clients ?? [];
      const fallbackClient = clients[0];
      const clientName = window
        .prompt("Client name", fallbackClient?.name ?? "")
        ?.trim();
      if (!clientName) return;

      const selectedClient = clients.find(
        (client) =>
          client.name.trim().toLowerCase() === clientName.toLowerCase()
      );
      if (!selectedClient) {
        app.patchAppState({
          snackbar: {
            message: `No client found for "${clientName}"`,
            severity: "error",
            open: true,
            type: "alert",
          },
        });
        return;
      }

      const userEmail = window
        .prompt(
          "User email",
          selectedClient.users[0]?.email ?? state.userProfile?.email ?? ""
        )
        ?.trim();
      if (!userEmail) return;

      try {
        const result = await generateDebugMagicLink({
          clientId: selectedClient.id,
          userEmail,
        });
        await navigator.clipboard.writeText(result.url);
        app.patchAppState({
          snackbar: {
            message: `Magic link copied (expires ${result.expiresAt})`,
            severity: "success",
            open: true,
            type: "snackbar",
          },
        });
      } catch (error) {
        console.error("Magic link generation failed:", error);
        app.patchAppState({
          snackbar: {
            message: "Failed to generate magic link",
            severity: "error",
            open: true,
            type: "alert",
          },
        });
      }
    });

    const route = app.createRoute({
      path: APP_ROUTES.admin,
      view,
      isOverlayChild: true,
    });

    route.on("load", async () => {
      await this.loadOverview(app.getAppState().language);
    });
  }

  /** Client-side guard + data load (backend PlatformAdminGuard is the real gate). */
  private async loadOverview(language: SupportedLanguage): Promise<void> {
    if (!this.app) return;

    const profile =
      this.app.getAppState().userProfile ?? (await fetchUserProfile());

    // Couldn't load the profile (transient): show an error, don't bounce a real admin out.
    if (!profile) {
      this.app.patchAppState({
        isAdminLoading: false,
        adminLoadError: getLocalizedMessage(
          language,
          "adminPanel.noValidSession"
        ),
      });
      return;
    }

    if (!isPlatformAdminProfile(profile)) {
      this.app.navigate(APP_ROUTES.homePortal);
      return;
    }

    this.app.patchAppState({
      userProfile: profile,
      isAdminLoading: true,
      adminLoadError: undefined,
    });

    const overview = await fetchAdminOverview();

    this.app.patchAppState({
      adminOverview: overview ?? undefined,
      isAdminLoading: false,
      adminLoadError: overview
        ? undefined
        : getLocalizedMessage(language, "adminPanel.failedToLoad"),
    });
  }
}
